import { type ReactNode, useId } from "react";

export interface UseWizardFieldMessageOptions {
  isInvalid?: boolean;
  errorMessage?: ReactNode;
  successMessage?: ReactNode;
  /** Caller-forwarded aria-describedby and/or extra ids (e.g. WizardRichTextArea's countId) to merge. */
  ariaDescribedBy?: string;
}

export interface UseWizardFieldMessageReturn {
  /** Pass to WizardFieldFooter's `messageId`. */
  messageId: string;
  /** Merged value for the RAC field root's aria-describedby; undefined when no message renders. */
  ariaDescribedBy: string | undefined;
}

/** Associates the WizardFieldFooter message with the field's input. The message `<p>` only
 *  renders for the active validation branch, so aria-describedby must reference it under the
 *  same condition — `hasMessage` mirrors the footer's render predicate exactly. */
export function useWizardFieldMessage({
  isInvalid,
  errorMessage,
  successMessage,
  ariaDescribedBy,
}: UseWizardFieldMessageOptions): UseWizardFieldMessageReturn {
  const messageId = useId();
  const hasMessage = isInvalid ? Boolean(errorMessage) : Boolean(successMessage);
  const merged = [hasMessage ? messageId : null, ariaDescribedBy].filter(Boolean).join(" ") || undefined;
  return { messageId, ariaDescribedBy: merged };
}
